/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useRef, useState } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { PromptInputContext } from '@atlassian/conversation-assistant-external-context/types';
import { PreviewCard } from '@atlassian/conversation-assistant-ui-components/ui/PreviewCard';

import { type UploadedFile } from '../../controllers/attachtment-store/UploadedFile';
import { useAllAttachmentsStore } from '../../controllers/attachtment-store/useAllAttachmentsStore';

import { FileCard } from './file-card';
import { SearchInactiveContent } from '../../../ui/chat-prompt-input/SearchInactiveContent';
import { useSearchMode } from '../../../ui/chat-prompt-input/useSearchMode';

const styles = cssMap({
	listWrapper: {
		display: 'flex',
		overflowX: 'auto',
		whiteSpace: 'nowrap',
		maxWidth: '100%',
		scrollBehavior: 'smooth',
		gap: token('space.150'),
		listStyle: 'none',
		margin: 0,
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.150'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
	},
	listItem: {
		marginBlockStart: 0,
		listStyle: 'none',
	},
});

const useAutoScrollOnDataChange = <T,>(data: T[]) => {
	const prevDataLength = useRef(data.length);
	const [isAutoScroll, setIsAutoScroll] = useState(false);

	const dataLength = data.length;

	useEffect(() => {
		if (prevDataLength.current < dataLength) {
			setIsAutoScroll(true);
		} else {
			setIsAutoScroll(false);
		}
		prevDataLength.current = dataLength;
	}, [dataLength]);

	return isAutoScroll;
};

export const InputContextListDisplay = ({
	attachtmentsStoreKey,
}: {
	attachtmentsStoreKey: string;
}): JSX.Element | null => {
	const searchMode = useSearchMode();
	const [attachmentsState, { removeExternalContext, removeFile }] =
		useAllAttachmentsStore(attachtmentsStoreKey);
	const isAutoScroll = useAutoScrollOnDataChange(attachmentsState);

	if (attachmentsState.length === 0) {
		return null;
	}

	return (
		<Box as="ul" xcss={styles.listWrapper}>
			{attachmentsState.map((context, index) => {
				const isLast = index === attachmentsState.length - 1;
				let key: string;
				let content: JSX.Element;

				switch (context.type) {
					case 'file':
						const file = context.data as UploadedFile;
						key = file.name;
						content = (
							<SearchInactiveContent as="div">
								<FileCard
									isDisabled={Boolean(searchMode)}
									fileName={file.name}
									onRemove={() => removeFile(attachtmentsStoreKey, file.name)}
									isLoading={file.isLoading}
									error={file.error}
									autoScroll={isAutoScroll && Boolean(file.error || isLast)}
									previewBlobUrl={file.previewBlobUrl}
									isPreviewLoading={file.isPreviewLoading}
									fileObject={file.fileObject}
								/>
							</SearchInactiveContent>
						);
						break;
					case 'external':
						const promptInputContext = context.data as PromptInputContext;
						const externalContext = promptInputContext.representation;
						key = promptInputContext.key;
						content = (
							<PreviewCard
								label={externalContext.label}
								subLabel={externalContext.description}
								onRemove={() => removeExternalContext(attachtmentsStoreKey, promptInputContext.key)}
								autoScroll={isAutoScroll && Boolean(isLast)}
								cardIcon={externalContext.image}
							/>
						);
						break;
					default:
						return null;
				}

				return (
					<Box as="li" key={key} xcss={styles.listItem}>
						{content}
					</Box>
				);
			})}
		</Box>
	);
};
